import { useState, useEffect, useMemo, useRef } from "react";
import * as XLSX from "xlsx";

const TERMII_API_KEY = "TLPxyMgMtiaEdCYKEhmwQXuVHIxfCWLWoEpiKsFBSUalyOPHSokzdTOKCHtcIN";
const TERMII_URL = "https://api.ng.termii.com/api/sms/send";
const DEFAULT_MESSAGE =
  "Having trouble paying your EDSAA permit? Update your mobile app on the Apple or Play Store to enjoy seamless payments. Update EDSAA-Adera today to pay with ease!";

const toProper = (s = "") =>
  s
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

const formatCurrency = (v) =>
  new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN" })
    .format(Number(v) || 0)
    .replace("NGN", "₦");

const STATUS_COLORS = {
  idle: { bg: "#f1f5f9", color: "#64748b", label: "Not Sent" },
  sending: { bg: "#fef3c7", color: "#d97706", label: "Sending…" },
  sent: { bg: "#dcfce7", color: "#16a34a", label: "Delivered" },
  error: { bg: "#fee2e2", color: "#dc2626", label: "Failed" },
};

export default function EdsaaSMSSender() {
  const [vendors, setVendors] = useState([]);
  const [loadingFile, setLoadingFile] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [statuses, setStatuses] = useState({}); // id -> { status, error }
  const [sending, setSending] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const fileRef = useRef();

  // Load the Excel from file input
  const handleFileLoad = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoadingFile(true);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: "binary" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
        const parsed = rows
          .slice(1)
          .filter((r) => r[0] && r[1] && r[2])
          .map((r) => ({
            id: String(r[0]),
            name: String(r[1]),
            phone: String(r[2]),
            price: String(r[3] || ""),
            address: String(r[4] || ""),
          }));
        setVendors(parsed);
        setStatuses({});
        setSelected(new Set());
      } catch (err) {
        alert("Failed to parse file: " + err.message);
      }
      setLoadingFile(false);
    };
    reader.readAsBinaryString(file);
  };

  const filtered = useMemo(() => {
    let list = vendors;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (v) =>
          v.name.toLowerCase().includes(q) ||
          v.phone.includes(q) ||
          (v.address || "").toLowerCase().includes(q)
      );
    }
    if (filterStatus !== "all") {
      list = list.filter((v) => {
        const s = statuses[v.id]?.status || "idle";
        return s === filterStatus;
      });
    }
    return list;
  }, [vendors, search, filterStatus, statuses]);

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (filtered.every((v) => selected.has(v.id))) {
      setSelected((prev) => {
        const next = new Set(prev);
        filtered.forEach((v) => next.delete(v.id));
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        filtered.forEach((v) => next.add(v.id));
        return next;
      });
    }
  };

  const selectedVendors = vendors.filter((v) => selected.has(v.id));

  const setStatus = (id, status, error = null) => {
    setStatuses((prev) => ({ ...prev, [id]: { status, error } }));
  };

  const handleSend = async () => {
    if (!selectedVendors.length) return;
    setSending(true);

    // mark all as sending
    selectedVendors.forEach((v) => setStatus(v.id, "sending"));

    for (const v of selectedVendors) {
      try {
        const res = await fetch(TERMII_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            to: v.phone,
            from: "ADERA",
            sms: message,
            type: "plain",
            api_key: TERMII_API_KEY,
            channel: "generic",
          }),
        });
        const data = await res.json();
        if (data?.message?.toLowerCase?.().includes("success") || res.ok) {
          setStatus(v.id, "sent");
        } else {
          setStatus(v.id, "error", data?.message || "Unknown error");
        }
      } catch (err) {
        setStatus(v.id, "error", err.message);
      }
    }

    setSending(false);
    setSelected(new Set());
  };

  const sentCount = Object.values(statuses).filter((s) => s.status === "sent").length;
  const failCount = Object.values(statuses).filter((s) => s.status === "error").length;
  const sendingCount = Object.values(statuses).filter((s) => s.status === "sending").length;
  const charCount = message.length;
  const smsSegments = Math.ceil(charCount / 160);

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((v) => selected.has(v.id));

  return (
    <div
      style={{
        fontFamily: "'Inter', sans-serif",
        minHeight: "100vh",
        background: "#f0f4f8",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "linear-gradient(90deg,#20AA3E 0%,#03A588 100%)",
          padding: "18px 28px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: "0 2px 12px rgba(0,0,0,.15)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: 10,
              background: "rgba(255,255,255,.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 20,
            }}
          >
            📨
          </div>
          <div>
            <div style={{ color: "#fff", fontWeight: 800, fontSize: 18 }}>
              EDSAA SMS Blast
            </div>
            <div style={{ color: "rgba(255,255,255,.75)", fontSize: 12 }}>
              Termii · App Update Reminder
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 20 }}>
          {[
            { label: "Total", val: vendors.length, col: "#fff" },
            { label: "Delivered", val: sentCount, col: "#a7f3d0" },
            { label: "Failed", val: failCount, col: "#fca5a5" },
            { label: "In Progress", val: sendingCount, col: "#fde68a" },
          ].map((s) => (
            <div key={s.label} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: s.col }}>
                {s.val}
              </div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,.7)", fontWeight: 600 }}>
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Load file bar */}
      {vendors.length === 0 && (
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 16,
            padding: 40,
          }}
        >
          <div
            style={{
              border: "2px dashed #cbd5e1",
              borderRadius: 18,
              padding: "48px 64px",
              textAlign: "center",
              background: "#fff",
              cursor: "pointer",
            }}
            onClick={() => fileRef.current.click()}
          >
            <div style={{ fontSize: 48, marginBottom: 12 }}>📂</div>
            <div style={{ fontWeight: 700, fontSize: 18, color: "#1e293b", marginBottom: 6 }}>
              Load Vendor List
            </div>
            <div style={{ color: "#94a3b8", fontSize: 13 }}>
              Click to upload the demand-notice-vendor-list XLSX file
            </div>
            {loadingFile && (
              <div style={{ marginTop: 16, color: "#006FEE", fontWeight: 600 }}>
                Loading…
              </div>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: "none" }}
            onChange={handleFileLoad}
          />
        </div>
      )}

      {/* Main split panel */}
      {vendors.length > 0 && (
        <div style={{ flex: 1, display: "flex", gap: 0, overflow: "hidden", height: "calc(100vh - 82px)" }}>
          {/* LEFT: Vendor Table */}
          <div
            style={{
              flex: "0 0 62%",
              display: "flex",
              flexDirection: "column",
              background: "#fff",
              borderRight: "1px solid #e2e8f0",
              overflow: "hidden",
            }}
          >
            {/* Table toolbar */}
            <div
              style={{
                padding: "14px 18px",
                borderBottom: "1px solid #f1f5f9",
                display: "flex",
                gap: 10,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div style={{ position: "relative", flex: "1 1 200px" }}>
                <span
                  style={{
                    position: "absolute",
                    left: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#94a3b8",
                    fontSize: 14,
                  }}
                >
                  🔍
                </span>
                <input
                  type="text"
                  placeholder="Search name, phone, address…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px 10px 8px 32px",
                    border: "1.5px solid #e2e8f0",
                    borderRadius: 8,
                    fontSize: 13,
                    outline: "none",
                    boxSizing: "border-box",
                    fontFamily: "inherit",
                  }}
                />
              </div>
              {/* Status filter pills */}
              <div style={{ display: "flex", gap: 6 }}>
                {[
                  { val: "all", label: "All" },
                  { val: "idle", label: "Not Sent" },
                  { val: "sent", label: "Delivered" },
                  { val: "error", label: "Failed" },
                ].map((f) => (
                  <button
                    key={f.val}
                    onClick={() => setFilterStatus(f.val)}
                    style={{
                      padding: "5px 12px",
                      borderRadius: 20,
                      fontSize: 12,
                      fontWeight: 600,
                      border: "none",
                      cursor: "pointer",
                      background: filterStatus === f.val ? "#006FEE" : "#f1f5f9",
                      color: filterStatus === f.val ? "#fff" : "#64748b",
                    }}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <div
                style={{
                  marginLeft: "auto",
                  fontSize: 12,
                  color: "#94a3b8",
                  whiteSpace: "nowrap",
                }}
              >
                {selected.size} selected · {filtered.length} shown of {vendors.length}
              </div>
              <label
                style={{
                  fontSize: 12,
                  color: "#64748b",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: "none" }}
                  onChange={handleFileLoad}
                />
                🔄 Reload File
              </label>
            </div>

            {/* Table */}
            <div style={{ flex: 1, overflowY: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 12,
                }}
              >
                <thead>
                  <tr
                    style={{
                      background: "#f8fafc",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: 700, color: "#64748b", borderBottom: "1px solid #e2e8f0", width: 36 }}>
                      <input
                        type="checkbox"
                        checked={allFilteredSelected}
                        onChange={toggleAll}
                        style={{ accentColor: "#006FEE", cursor: "pointer" }}
                      />
                    </th>
                    <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: 700, color: "#64748b", borderBottom: "1px solid #e2e8f0" }}>
                      Business Name
                    </th>
                    <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: 700, color: "#64748b", borderBottom: "1px solid #e2e8f0" }}>
                      Phone Number
                    </th>
                    <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: 700, color: "#64748b", borderBottom: "1px solid #e2e8f0" }}>
                      Amount Due
                    </th>
                    <th style={{ padding: "10px 14px", textAlign: "center", fontWeight: 700, color: "#64748b", borderBottom: "1px solid #e2e8f0" }}>
                      SMS Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        style={{ textAlign: "center", padding: 40, color: "#94a3b8" }}
                      >
                        No vendors found
                      </td>
                    </tr>
                  )}
                  {filtered.map((v, idx) => {
                    const s = statuses[v.id] || { status: "idle" };
                    const sc = STATUS_COLORS[s.status] || STATUS_COLORS.idle;
                    const isSelected = selected.has(v.id);
                    return (
                      <tr
                        key={v.id}
                        onClick={() => toggleOne(v.id)}
                        style={{
                          background: isSelected ? "#f0f7ff" : idx % 2 === 0 ? "#fff" : "#fafafa",
                          cursor: "pointer",
                          borderBottom: "1px solid #f1f5f9",
                          transition: "background .1s",
                        }}
                      >
                        <td style={{ padding: "9px 14px" }}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}}
                            style={{ accentColor: "#006FEE", cursor: "pointer" }}
                          />
                        </td>
                        <td style={{ padding: "9px 14px", fontWeight: 600, color: "#1e293b" }}>
                          {toProper(v.name)}
                          {v.address && (
                            <div
                              style={{
                                fontSize: 11,
                                color: "#94a3b8",
                                fontWeight: 400,
                                marginTop: 1,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: 220,
                              }}
                            >
                              {toProper(v.address)}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: "9px 14px", color: "#334155", fontFamily: "monospace", fontSize: 12 }}>
                          {v.phone}
                        </td>
                        <td style={{ padding: "9px 14px", fontWeight: 700, color: "#0f172a" }}>
                          {v.price ? formatCurrency(v.price) : "—"}
                        </td>
                        <td style={{ padding: "9px 14px", textAlign: "center" }}>
                          <span
                            style={{
                              display: "inline-block",
                              padding: "3px 10px",
                              borderRadius: 20,
                              fontSize: 11,
                              fontWeight: 700,
                              background: sc.bg,
                              color: sc.color,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {sc.label}
                            {s.status === "error" && s.error && (
                              <span
                                title={s.error}
                                style={{ marginLeft: 4, cursor: "help" }}
                              >
                                ⚠
                              </span>
                            )}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* RIGHT: Message Composer */}
          <div
            style={{
              flex: "0 0 38%",
              display: "flex",
              flexDirection: "column",
              background: "#f8fafc",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "20px 24px",
                borderBottom: "1px solid #e2e8f0",
                background: "#fff",
              }}
            >
              <div
                style={{ fontWeight: 800, fontSize: 15, color: "#0f172a", marginBottom: 2 }}
              >
                Message Composer
              </div>
              <div style={{ fontSize: 12, color: "#94a3b8" }}>
                Sending via Termii · Sender ID: <strong>ADERA</strong>
              </div>
            </div>

            <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px", display: "flex", flexDirection: "column", gap: 18 }}>
              {/* Message body */}
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 8,
                  }}
                >
                  <label
                    style={{ fontWeight: 700, fontSize: 13, color: "#334155" }}
                  >
                    Message Body
                  </label>
                  <button
                    onClick={() => setMessage(DEFAULT_MESSAGE)}
                    style={{
                      background: "none",
                      border: "none",
                      fontSize: 11,
                      color: "#006FEE",
                      cursor: "pointer",
                      fontWeight: 600,
                      padding: 0,
                    }}
                  >
                    Reset
                  </button>
                </div>
                <textarea
                  rows={7}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  style={{
                    width: "100%",
                    border: "1.5px solid #e2e8f0",
                    borderRadius: 10,
                    padding: "12px 14px",
                    fontSize: 13,
                    color: "#334155",
                    resize: "vertical",
                    fontFamily: "inherit",
                    outline: "none",
                    lineHeight: 1.6,
                    boxSizing: "border-box",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#006FEE")}
                  onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
                />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    fontSize: 11,
                    color: "#94a3b8",
                    marginTop: 4,
                  }}
                >
                  {charCount} chars · {smsSegments} SMS segment
                  {smsSegments > 1 ? "s" : ""}
                </div>
              </div>

              {/* Preview bubble */}
              <div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#94a3b8",
                    textTransform: "uppercase",
                    letterSpacing: ".8px",
                    marginBottom: 10,
                  }}
                >
                  Preview
                </div>
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 16,
                    padding: 16,
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 2px 8px rgba(0,0,0,.05)",
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: "#94a3b8",
                      marginBottom: 8,
                      fontWeight: 600,
                    }}
                  >
                    From: ADERA
                  </div>
                  <div
                    style={{
                      background: "#e8f4fd",
                      borderRadius: "12px 12px 12px 4px",
                      padding: "12px 14px",
                      fontSize: 13,
                      color: "#0f172a",
                      lineHeight: 1.7,
                    }}
                  >
                    {message}
                  </div>
                </div>
              </div>

              {/* Selection summary */}
              <div
                style={{
                  background: "#fff",
                  borderRadius: 12,
                  padding: "14px 16px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#94a3b8",
                    textTransform: "uppercase",
                    letterSpacing: ".8px",
                    marginBottom: 10,
                  }}
                >
                  Send Summary
                </div>
                {[
                  { label: "Recipients selected", val: selected.size, col: "#006FEE" },
                  { label: "SMS segments/recipient", val: smsSegments, col: "#334155" },
                  { label: "Est. total SMS sent", val: selected.size * smsSegments, col: "#334155" },
                  { label: "Delivered", val: sentCount, col: "#16a34a" },
                  { label: "Failed", val: failCount, col: "#dc2626" },
                ].map((row) => (
                  <div
                    key={row.label}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "5px 0",
                      borderBottom: "1px solid #f1f5f9",
                      fontSize: 13,
                    }}
                  >
                    <span style={{ color: "#64748b" }}>{row.label}</span>
                    <span style={{ fontWeight: 800, color: row.col }}>{row.val}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Send button footer */}
            <div
              style={{
                padding: "16px 24px",
                borderTop: "1px solid #e2e8f0",
                background: "#fff",
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              {/* Progress bar if sending */}
              {sending && (
                <div
                  style={{
                    height: 6,
                    background: "#e2e8f0",
                    borderRadius: 6,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      borderRadius: 6,
                      background: "linear-gradient(90deg,#22c55e,#16a34a)",
                      width: `${
                        selectedVendors.length
                          ? ((sentCount + failCount) / selectedVendors.length) * 100
                          : 0
                      }%`,
                      transition: "width .4s ease",
                    }}
                  />
                </div>
              )}

              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={() => {
                    setSelected(new Set(vendors.map((v) => v.id)));
                  }}
                  style={{
                    flex: 1,
                    padding: "11px 0",
                    borderRadius: 10,
                    border: "1.5px solid #e2e8f0",
                    background: "#f1f5f9",
                    fontWeight: 700,
                    fontSize: 13,
                    cursor: "pointer",
                    color: "#334155",
                  }}
                >
                  Select All ({vendors.length})
                </button>
                <button
                  onClick={() => setSelected(new Set())}
                  style={{
                    padding: "11px 16px",
                    borderRadius: 10,
                    border: "1.5px solid #fee2e2",
                    background: "#fff1f2",
                    fontWeight: 700,
                    fontSize: 13,
                    cursor: "pointer",
                    color: "#ef4444",
                  }}
                >
                  Clear
                </button>
              </div>

              <button
                onClick={handleSend}
                disabled={sending || selected.size === 0 || !message.trim()}
                style={{
                  width: "100%",
                  padding: "14px 0",
                  borderRadius: 12,
                  border: "none",
                  background:
                    sending || selected.size === 0
                      ? "#94a3b8"
                      : "linear-gradient(135deg,#006FEE,#0050c8)",
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: 15,
                  cursor:
                    sending || selected.size === 0 ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  boxShadow:
                    !sending && selected.size > 0
                      ? "0 6px 20px rgba(0,111,238,.35)"
                      : "none",
                  transition: "all .2s",
                }}
              >
                {sending ? (
                  <>⏳ Sending to {selectedVendors.length} recipients…</>
                ) : (
                  <>📨 Send SMS to {selected.size} Recipient{selected.size !== 1 ? "s" : ""}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
